import { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const THEMES = [
  {
    id: 'light',
    name: 'Light',
    description: 'Clean white background',
    preview: { bg: '#ffffff', text: '#111827', accent: '#2563eb' }
  },
  {
    id: 'dark',
    name: 'Dark',
    description: 'Modern dark appearance',
    preview: { bg: '#111827', text: '#f9fafb', accent: '#60a5fa' }
  },
  {
    id: 'brand',
    name: 'Brand',
    description: 'Use your brand colors',
    preview: { bg: '#f0f9ff', text: '#1e40af', accent: '#3b82f6' }
  }
]

function ThemeSelector({ value, onChange, colors = {}, onColorChange }) {
  const { t } = useTranslation('ecommerce')

  const handleColorChange = useCallback((field, newValue) => {
    if (onColorChange) {
      onColorChange({
        ...colors,
        [field]: newValue
      })
    }
  }, [colors, onColorChange])

  return (
    <div className="space-y-4">
      <div
        role="radiogroup"
        aria-label={t('bioPage.theme.label', 'Theme selection')}
        className="grid grid-cols-3 gap-3"
      >
        {THEMES.map((theme) => (
          <label
            key={theme.id}
            className={`relative flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
              value === theme.id
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="theme"
              value={theme.id}
              checked={value === theme.id}
              onChange={() => onChange(theme.id)}
              className="sr-only"
              aria-label={theme.name}
            />
            {/* Theme Preview */}
            <div
              data-testid={`theme-preview-${theme.id}`}
              className="w-full h-16 rounded-md mb-2 flex items-center justify-center text-xs"
              style={{
                backgroundColor: theme.preview.bg,
                color: theme.preview.text
              }}
            >
              <div
                className="w-6 h-6 rounded"
                style={{ backgroundColor: theme.preview.accent }}
              />
            </div>
            {/* Theme Name */}
            <span className="text-sm font-medium text-gray-900">
              {t(`bioPage.theme.${theme.id}`, theme.name)}
            </span>
            {/* Checkmark for selected */}
            {value === theme.id && (
              <svg
                className="absolute top-1 right-1 w-5 h-5 text-primary-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </label>
        ))}
      </div>

      {/* Color Customization for Brand Theme */}
      {value === 'brand' && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
          <h4 className="text-sm font-medium text-gray-700">
            {t('bioPage.theme.customColors', 'Custom Colors')}
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="primary-color"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                {t('bioPage.theme.primaryColor', 'Primary Color')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="primary-color"
                  value={colors.primary || '#3b82f6'}
                  onChange={(e) => handleColorChange('primary', e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                />
                <input
                  type="text"
                  value={colors.primary || '#3b82f6'}
                  onChange={(e) => handleColorChange('primary', e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded"
                  aria-label={t('bioPage.theme.primaryColor', 'Primary Color')}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="background-color"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                {t('bioPage.theme.backgroundColor', 'Background Color')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="background-color"
                  value={colors.background || '#ffffff'}
                  onChange={(e) => handleColorChange('background', e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                />
                <input
                  type="text"
                  value={colors.background || '#ffffff'}
                  onChange={(e) => handleColorChange('background', e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded"
                  aria-label={t('bioPage.theme.backgroundColor', 'Background Color')}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

ThemeSelector.propTypes = {
  value: PropTypes.oneOf(['light', 'dark', 'brand']).isRequired,
  onChange: PropTypes.func.isRequired,
  colors: PropTypes.shape({
    primary: PropTypes.string,
    background: PropTypes.string,
  }),
  onColorChange: PropTypes.func,
}

export default ThemeSelector
